import React, { useState, useEffect } from "react";
import { Form, FormGroup, Input, Label, Row, Col, Button } from "reactstrap";
import Select from "react-select";
import Swal from "sweetalert2";
import API_URL from '../config';
import { saveEmployee } from "./Api/GmsEmployee";

// Form helper components
const FormSelect = ({ label, id, name, options, value, onChange, error, required = true, isDisabled = false }) => (
  <div className="form-group">
    <Label className="col-form-label" htmlFor={id}>
      {label} {required && '*'}
    </Label>
    <Select
      id={id}
      name={name}
      options={options}
      value={value}
      onChange={onChange}
      isClearable={!isDisabled}
      isSearchable
      isDisabled={isDisabled}
      placeholder={`Select ${label}`}
      classNamePrefix="select2-selection"
      className={error ? 'is-invalid' : ''}
    />
    {error && (
      <div className="invalid-feedback d-block">
        {error}
      </div>
    )}
  </div>
);

const FormInput = ({ label, id, name, type = "text", value, onChange, error, required = true, placeholder, disabled = false, readOnly = false, className = "" }) => (
  <div className="form-group">
    <Label className="col-form-label" htmlFor={id}>
      {label} {required && '*'}
    </Label>
    <Input
      type={type}
      className={`form-control ${error ? 'is-invalid' : ''} ${className}`}
      id={id}
      name={name}
      value={value || ''}
      onChange={onChange}
      placeholder={placeholder}
      required={required}
      disabled={disabled}
      readOnly={readOnly}
    />
    {error && (
      <div className="invalid-feedback d-block">
        {error}
      </div>
    )}
  </div>
);

const FormCheckbox = ({ label, id, name, checked, onChange, disabled = false }) => (
  <div className="form-group mt-4">
    <div className="form-check form-switch">
      <input
        type="checkbox"
        className="form-check-input"
        id={id}
        name={name}
        checked={checked}
        onChange={onChange}
        disabled={disabled}
      />
      <label className="form-check-label" htmlFor={id}>
        {label === "Active" ? (checked ? "Active" : "Inactive") : label}
      </label>
    </div>
  </div>
);

const FormBenefitCheckbox = ({ label, checkboxName, amountName, checkboxChecked, amountValue, onCheckboxChange, onAmountChange }) => (
  <div className="benefit-item">
    <div className="form-check">
      <Input
        type="checkbox"
        className="form-check-input"
        id={checkboxName}
        name={checkboxName}
        checked={checkboxChecked}
        onChange={onCheckboxChange}
      />
      <Label className="form-check-label" htmlFor={checkboxName}>
        {label}
      </Label>
    </div>
    {checkboxChecked && (
      <div className="mt-2">
        <Input
          type="number"
          name={amountName}
          value={amountValue || 0}
          onChange={onAmountChange}
          placeholder="Enter amount"
          min="0"
          step="any"
          className="form-control-sm"
        />
      </div>
    )}
  </div>
);

const ImagePreview = ({ file, existingImage }) => {
  if (!file && !existingImage) return null;

  let imageUrl;
  if (file) {
    imageUrl = URL.createObjectURL(file);
  } else if (existingImage) {
    imageUrl = existingImage.startsWith('http') ? existingImage : `${API_URL}${existingImage}`;
  }
  
  return (
    <div className="mt-2">
      <img 
        src={imageUrl} 
        alt="Employee" 
        style={{ 
          maxWidth: '200px', 
          maxHeight: '200px', 
          objectFit: 'cover',
          borderRadius: '8px'
        }} 
      />
    </div>
  );
};

export const defaultEmployeeForm = {
  employee_Id: null,
  employee_Code: "",
  employee_NameEn: "",
  employee_NameAr: "",
  employee_Business_Id: null,
  employee_Department_ID: null,
  employee_Designation_ID: null,
  employee_Branch_Id: null,
  employee_Gender_Id: null,
  employee_Country_Id: null,
  employee_Address1: "",
  employee_Address2: "",
  employee_BirthDate: "",
  employee_JoinDate: "",
  employee_NatID: "",
  employee_NatID_Date: "",
  employee_NatID_ExpDate: "",
  employee_PassportNumber: "",
  employee_Passport_Date: "",
  employee_Passport_ExpDate: "",
  employee_VisaNumber: "",
  employee_Visa_Date: "",
  employee_Visa_ExpDate: "",
  employee_ServiceEndDate: "",
  employee_BS: 0,
  employee_HRA: 0,
  employee_TA: 0,
  employee_OA: 0,
  employee_GS: 0,
  employee_IsOnProbation: true,
  employee_CreatedDate: new Date(),
  employee_CreatedBy: Number(localStorage.getItem("userId")) || 1,
  employee_ModifiedDate: new Date(),
  employee_ModifiedBy: Number(localStorage.getItem("userId")) || 1,
  employee_ImagePath: null,
  employee_Email: "",
  employee_LabourCardNumber: "",
  employee_LabourCardExpiry: "",
  employee_EmploymentType: "Permanent",
  employee_FoodAllowance: 0,
  employee_CommunicationAllowance: 0,
  employee_UtilityAllowance: 0,
  employee_HousingProvided: false,
  employee_TelephoneProvided: false,
  employee_FoodProvided: false,
  employee_OtherBenefits: false,
  employee_HousingAmount: 0,
  employee_TelephoneAmount: 0,
  employee_FoodAmount: 0,
  employee_OtherBenefitsAmount: 0,
  employee_ContactNumber1: "",
  employee_ContactNumber2: "",
  onWork: true,
  emp_No: "",
  wpsSalary: 0,
};

const EmployeeForm = ({
  form,
  setForm,
  errors,
  setErrors,
  loading,
  setLoading,
  isEditing = false,
  onSuccess,
  onCancel,
  genders = [],
  countries = [],
  businesses = [],
  departments = [],
  designations = [],
  branches = [],
  hasDeletePermission = false,
  isCompanyDisabled = false,
  isBranchDisabled = false
}) => {
  const [selectedImage, setSelectedImage] = useState(null);

  // Generate options for dropdowns
  const genderOptions = genders.map(gender => ({
    value: gender.gender_Id,
    label: `${gender.gender_NameEn} - ${gender.gender_NameAr}`
  }));

  const countryOptions = countries.map(country => ({
    value: country.country_Id,
    label: `${country.country_NameEn} - ${country.country_NameAr}`
  }));

  const businessOptions = businesses.map(business => ({
    value: business.business_Id,
    label: `${business.business_NameEn} - ${business.business_NameAr}`
  }));

  const departmentOptions = departments.map(dept => ({
    value: dept.department_Id,
    label: `${dept.department_NameEn} - ${dept.department_NameAr}`
  }));

  const designationOptions = designations.map(des => ({
    value: des.designation_Id,
    label: `${des.designation_NameEn} - ${des.designation_NameAr}`
  }));

  // Branches are already filtered by business in the parent component
  const branchOptions = branches.map(branch => ({
    value: branch.branch_Id,
    label: `${branch.branch_NameEn} - ${branch.branch_NameAr}`
  }));

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    let processedValue = type === "checkbox" ? checked : value;

    // Handle numeric fields
    if (['employee_BS', 'employee_HRA', 'employee_TA', 'employee_OA', 'wpsSalary',
         'employee_FoodAllowance', 'employee_CommunicationAllowance', 'employee_UtilityAllowance',
         'employee_HousingAmount', 'employee_TelephoneAmount', 'employee_FoodAmount', 'employee_OtherBenefitsAmount'].includes(name)) {
      if (value < 0) {
        setErrors(prev => ({
          ...prev,
          [name]: "Amount cannot be negative"
        }));
        return;
      }
      processedValue = value === '' ? 0 : parseFloat(value) || 0;

      if (name === 'wpsSalary') {
        const currentGrossSalary = 
          Number(form.employee_BS || 0) +
          Number(form.employee_HRA || 0) +
          Number(form.employee_TA || 0) +
          Number(form.employee_OA || 0) +
          Number(form.employee_FoodAllowance || 0) +
          Number(form.employee_CommunicationAllowance || 0) +
          Number(form.employee_UtilityAllowance || 0);

        if (processedValue > currentGrossSalary) {
          setErrors(prev => ({
            ...prev,
            [name]: "WPS Salary cannot exceed Gross Salary"
          }));
          return;
        }
      }
    }

    const updatedForm = { ...form, [name]: processedValue };

    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }

    if (['employee_BS', 'employee_HRA', 'employee_TA', 'employee_OA',
         'employee_FoodAllowance', 'employee_CommunicationAllowance', 'employee_UtilityAllowance'].includes(name)) {
      const grossSalary = 
        Number(updatedForm.employee_BS || 0) +
        Number(updatedForm.employee_HRA || 0) +
        Number(updatedForm.employee_TA || 0) +
        Number(updatedForm.employee_OA || 0) +
        Number(updatedForm.employee_FoodAllowance || 0) +
        Number(updatedForm.employee_CommunicationAllowance || 0) +
        Number(updatedForm.employee_UtilityAllowance || 0);

      updatedForm.employee_GS = grossSalary;

      if (Number(updatedForm.wpsSalary || 0) > grossSalary) {
        setErrors(prev => ({
          ...prev,
          wpsSalary: "WPS Salary cannot exceed Gross Salary"
        }));
        updatedForm.wpsSalary = grossSalary;
      }
    }

    setForm(updatedForm);
  };

  const handleSelectFormChange = (selectedOption, field) => {
    setForm(prevForm => {
      const updatedForm = {
        ...prevForm,
        [field]: selectedOption ? selectedOption.value : null
      };
      
      if (field === 'employee_Business_Id') {
        updatedForm.employee_Branch_Id = null;
      }
      
      return updatedForm;
    });
  };

  const handleImageChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setSelectedImage(file);
    }
  };

  const copyAddress1ToAddress2 = () => {
    setForm(prevForm => ({
      ...prevForm,
      employee_Address2: prevForm.employee_Address1
    }));
  };

  const validateMandatoryFields = () => {
    const newErrors = {};

    if (!form.employee_NameEn?.trim()) {
      newErrors.employee_NameEn = "Employee Name (English) is required";
    }
    if (!form.emp_No?.trim()) {
      newErrors.emp_No = "Employee Number is required";
    }
    if (form.emp_No && form.emp_No.length > 10) {
      newErrors.emp_No = "Employee Number cannot exceed 10 characters";
    }
    if (!form.employee_Gender_Id) {
      newErrors.employee_Gender_Id = "Gender is required";
    }
    if (!form.employee_ContactNumber1?.trim()) {
      newErrors.employee_ContactNumber1 = "Primary Contact Number is required";
    }
    if (!form.employee_NatID?.trim()) {
      newErrors.employee_NatID = "National ID is required";
    }
    if (!form.employee_NatID_ExpDate) {
      newErrors.employee_NatID_ExpDate = "National ID Expiry Date is required";
    }
    if (!form.employee_PassportNumber?.trim()) {
      newErrors.employee_PassportNumber = "Passport Number is required";
    }
    if (!form.employee_Passport_ExpDate) {
      newErrors.employee_Passport_ExpDate = "Passport Expiry Date is required";
    }
    if (!form.employee_VisaNumber?.trim()) {
      newErrors.employee_VisaNumber = "Visa Number is required";
    }
    if (!form.employee_Visa_ExpDate) {
      newErrors.employee_Visa_ExpDate = "Visa Expiry Date is required";
    }
    if (!form.employee_LabourCardNumber?.trim()) {
      newErrors.employee_LabourCardNumber = "Labour Card Number is required";
    }
    if (!form.employee_LabourCardExpiry) {
      newErrors.employee_LabourCardExpiry = "Labour Card Expiry Date is required";
    }
    if (!form.employee_JoinDate) {
      newErrors.employee_JoinDate = "Joining Date is required";
    }
    if (!form.employee_BS || form.employee_BS <= 0) {
      newErrors.employee_BS = "Basic Salary is required and must be greater than 0";
    }
    if (!form.employee_HRA || form.employee_HRA < 0) {
      newErrors.employee_HRA = "HRA is required and cannot be negative";
    }
    if (!form.wpsSalary || form.wpsSalary <= 0) {
      newErrors.wpsSalary = "WPS Salary is required and must be greater than 0";
    }
    if (!form.employee_Address1?.trim()) {
      newErrors.employee_Address1 = "Address 1 is required";
    }
    if (!form.employee_Business_Id) {
      newErrors.employee_Business_Id = "Business is required";
    }
    if (!form.employee_Department_ID) {
      newErrors.employee_Department_ID = "Department is required";
    }
    if (!form.employee_Designation_ID) {
      newErrors.employee_Designation_ID = "Designation is required";
    }
    if (!form.employee_Branch_Id) {
      newErrors.employee_Branch_Id = "Branch is required";
    }
    if (!form.employee_BirthDate) {
      newErrors.employee_BirthDate = "Birth Date is required";
    }

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (loading) {
      return;
    }
    
    setLoading(true);
    
    const validationErrors = validateMandatoryFields();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      Swal.fire("Validation Error", "Please fill in all mandatory fields", "error");
      setLoading(false);
      return;
    }
    
    try {
      const formData = new FormData();
      
      if (selectedImage) {
        formData.append('imageFile', selectedImage);
      } else if (isEditing && form.employee_ImagePath) {
        const dummyFile = new File([''], 'dummy.txt', { type: 'text/plain' });
        formData.append('imageFile', dummyFile);
        formData.append('keepExistingImage', 'true');
      }
      
      const fieldMapping = {
        'employee_Id': 'Employee_Id',
        'employee_Code': 'Employee_Code', 
        'employee_NameEn': 'Employee_NameEn',
        'employee_NameAr': 'Employee_NameAr',
        'employee_Gender_Id': 'Employee_Gender_Id',
        'employee_BirthDate': 'Employee_BirthDate',
        'employee_JoinDate': 'Employee_JoinDate',
        'employee_PassportNumber': 'Employee_PassportNumber',
        'employee_NatID': 'Employee_NatID',
        'employee_VisaNumber': 'Employee_VisaNumber',
        'employee_Passport_Date': 'Employee_Passport_Date',
        'employee_Passport_ExpDate': 'Employee_Passport_ExpDate',
        'employee_NatID_Date': 'Employee_NatID_Date',
        'employee_NatID_ExpDate': 'Employee_NatID_ExpDate',
        'employee_Visa_Date': 'Employee_Visa_Date',
        'employee_Visa_ExpDate': 'Employee_Visa_ExpDate',
        'employee_Country_Id': 'Employee_Country_Id',
        'employee_Address1': 'Employee_Address1',
        'employee_Address2': 'Employee_Address2',
        'employee_Department_ID': 'Employee_Department_ID',
        'employee_Designation_ID': 'Employee_Designation_ID',
        'employee_ServiceEndDate': 'Employee_ServiceEndDate',
        'employee_CreatedDate': 'Employee_CreatedDate',
        'employee_CreatedBy': 'Employee_CreatedBy',
        'employee_ModifiedDate': 'Employee_ModifiedDate',
        'employee_ModifiedBy': 'Employee_ModifiedBy',
        'employee_Business_Id': 'Employee_Business_Id',
        'employee_Branch_Id': 'Employee_Branch_Id',
        'employee_BS': 'Employee_BS',
        'employee_HRA': 'Employee_HRA',
        'employee_TA': 'Employee_TA',
        'employee_OA': 'Employee_OA',
        'employee_GS': 'Employee_GS',
        'employee_IsOnProbation': 'Employee_IsOnProbation',
        'employee_ImagePath': 'Employee_ImagePath',
        'employee_ContactNumber1': 'Employee_ContactNumber1',
        'employee_ContactNumber2': 'Employee_ContactNumber2',
        'employee_Email': 'Employee_Email',
        'employee_LabourCardNumber': 'Employee_LabourCardNumber',
        'employee_LabourCardExpiry': 'Employee_LabourCardExpiry',
        'employee_EmploymentType': 'Employee_EmploymentType',
        'employee_FoodAllowance': 'Employee_FoodAllowance',
        'employee_CommunicationAllowance': 'Employee_CommunicationAllowance',
        'employee_UtilityAllowance': 'Employee_UtilityAllowance',
        'employee_HousingProvided': 'Employee_HousingProvided',
        'employee_HousingAmount': 'Employee_HousingAmount',
        'employee_TelephoneProvided': 'Employee_TelephoneProvided',
        'employee_TelephoneAmount': 'Employee_TelephoneAmount',
        'employee_FoodProvided': 'Employee_FoodProvided',
        'employee_FoodAmount': 'Employee_FoodAmount',
        'employee_OtherBenefits': 'Employee_OtherBenefits',
        'employee_OtherBenefitsAmount': 'Employee_OtherBenefitsAmount',
        'onWork': 'OnWork',
        'emp_No': 'Emp_No',
        'wpsSalary': 'WpsSalary'
      };

      Object.keys(form).forEach(key => {
        const backendFieldName = fieldMapping[key] || key;
        
        if (key.includes('Date')) {
          if (form[key]) {
            const dateValue = new Date(form[key]).toISOString();
            formData.append(backendFieldName, dateValue);
          } else {
            if (['employee_CreatedDate', 'employee_ModifiedDate'].includes(key)) {
              const currentDate = new Date().toISOString();
              formData.append(backendFieldName, currentDate);
            } else {
              formData.append(backendFieldName, '');
            }
          }
        } else {
          let value = form[key];
          
          if (['employee_Business_Id', 'employee_Department_ID', 'employee_Designation_ID', 
               'employee_Branch_Id', 'employee_Gender_Id', 'employee_Country_Id',
               'employee_CreatedBy', 'employee_ModifiedBy'].includes(key)) {
            value = value || 0;
          }
          
          if (['employee_IsOnProbation', 'onWork', 'employee_HousingProvided', 
               'employee_TelephoneProvided', 'employee_FoodProvided', 'employee_OtherBenefits'].includes(key)) {
            value = value === true || value === 'true' ? true : false;
          }
          
          if (['employee_BS', 'employee_HRA', 'employee_TA', 'employee_OA', 'employee_GS',
               'wpsSalary', 'employee_FoodAllowance', 'employee_CommunicationAllowance',
               'employee_UtilityAllowance', 'employee_HousingAmount', 'employee_TelephoneAmount',
               'employee_FoodAmount', 'employee_OtherBenefitsAmount'].includes(key)) {
            value = value || 0;
          }
          
          formData.append(backendFieldName, value !== null && value !== undefined ? value : '');
        }
      });
      
      const response = await fetch(`${API_URL}/api/Employee/save`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: formData
      });
      
      if (!response.ok) {
        if (response.status === 401) {
          const { handleSessionTimeout } = await import('./Api/SessionTimeoutHandler');
          handleSessionTimeout();
          throw new Error('Session timed out. Please login again.');
        }
        
        let errorMessage = 'Failed to save employee';
        try {
          const errorResponse = await response.json();
          errorMessage = errorResponse.msg || errorResponse.message || errorMessage;
        } catch (e) {
          console.error('Could not parse error response:', e);
        }
        
        throw new Error(errorMessage);
      }
      
      const result = await response.json();
      
      if (result.id) {
        Swal.fire("Success", result.msg || "Employee saved successfully", "success");
        if (onSuccess) {
          onSuccess();
        }
      } else {
        Swal.fire("Error", result.msg || "Failed to save employee", "error");
      }
    } catch (error) {
      console.error('Error saving employee:', error);
      Swal.fire("Error", `Failed to save employee: ${error.message}`, "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Form onSubmit={handleSubmit}>
      {/* Personal Information Section */}
      <h6 className="mb-2 text-primary">Personal Information</h6>
      <Row className="mb-3">
        <Col lg="3">
          <div className="form-group">
            <Label className="col-form-label" htmlFor="emp_No">
              Employee Number *
            </Label>
            <Input
              type="text"
              className={`form-control ${errors.emp_No ? 'is-invalid' : ''}`}
              id="emp_No"
              name="emp_No"
              value={form.emp_No || ''}
              onChange={handleFormChange}
              placeholder="Employee Number"
              maxLength={10}
              required={true}
            />
            {errors.emp_No && (
              <div className="invalid-feedback d-block">
                {errors.emp_No}
              </div>
            )}
          </div>
        </Col>

        <Col lg="3">
          <FormInput
            label="Name (English)"
            id="employee_NameEn"
            name="employee_NameEn"
            value={form.employee_NameEn}
            onChange={handleFormChange}
            error={errors.employee_NameEn}
            placeholder="Name in English"
          />
        </Col>

        <Col lg="3">
          <FormInput
            label="Name (Arabic)"
            id="employee_NameAr"
            name="employee_NameAr"
            value={form.employee_NameAr}
            onChange={handleFormChange}
            error={errors.employee_NameAr}
            placeholder="Name in Arabic"
            required={false}
          />
        </Col>

        <Col lg="3">
          <FormSelect
            label="Gender"
            id="employee_Gender_Id"
            name="employee_Gender_Id"
            options={genderOptions}
            value={genderOptions.find(option => option.value === form.employee_Gender_Id) || null}
            onChange={(selectedOption) => handleSelectFormChange(selectedOption, 'employee_Gender_Id')}
            error={errors.employee_Gender_Id}
          />
        </Col>
      </Row>

      <Row className="mb-3">
        <Col lg="4">
          <FormCheckbox
            label="On Work (Active Employee)"
            id="onWork"
            name="onWork"
            checked={form.onWork}
            onChange={handleFormChange}
            disabled={isEditing && !hasDeletePermission}
          />
        </Col>
      </Row>

      {/* Contact Information */}
      <h6 className="mb-2 text-primary">Contact Information</h6>
      <Row className="mb-3">
        <Col lg="4">
          <FormInput
            label="Primary Contact"
            id="employee_ContactNumber1"
            name="employee_ContactNumber1"
            type="tel"
            value={form.employee_ContactNumber1}
            onChange={(e) => {
              // Only allow numeric input
              const numericValue = e.target.value.replace(/\D/g, '');
              handleFormChange({
                ...e,
                target: {
                  ...e.target,
                  name: e.target.name,
                  value: numericValue
                }
              });
            }}
            error={errors.employee_ContactNumber1}
            placeholder="Primary Contact Number"
            required={true}
          />
        </Col>

        <Col lg="4">
          <FormInput
            label="Secondary Contact"
            id="employee_ContactNumber2"
            name="employee_ContactNumber2"
            type="tel"
            value={form.employee_ContactNumber2}
            onChange={(e) => {
              // Only allow numeric input
              const numericValue = e.target.value.replace(/\D/g, '');
              handleFormChange({
                ...e,
                target: {
                  ...e.target,
                  name: e.target.name,
                  value: numericValue
                }
              });
            }}
            error={errors.employee_ContactNumber2}
            placeholder="Secondary Contact Number"
            required={false}
          />
        </Col>

        <Col lg="4">
          <FormInput
            label="Email"
            id="employee_Email"
            name="employee_Email"
            type="email"
            value={form.employee_Email}
            onChange={handleFormChange}
            error={errors.employee_Email}
            placeholder="Enter Email"
            required={false}
          />
        </Col>
      </Row>

      <Row className="mb-3">
        <Col lg="4">
          <FormInput
            label="Birth Date"
            id="employee_BirthDate"
            name="employee_BirthDate"
            type="date"
            value={form.employee_BirthDate}
            onChange={handleFormChange}
            error={errors.employee_BirthDate}
            required={true}
          />
        </Col>

        <Col lg="4">
          <FormSelect
            label="Country"
            id="employee_Country_Id"
            name="employee_Country_Id"
            options={countryOptions}
            value={countryOptions.find(option => option.value === form.employee_Country_Id) || null}
            onChange={(selectedOption) => handleSelectFormChange(selectedOption, 'employee_Country_Id')}
            error={errors.employee_Country_Id}
            required={false}
          />
        </Col>

        <Col lg="4">
          <FormInput
            label="National ID"
            id="employee_NatID"
            name="employee_NatID"
            value={form.employee_NatID}
            onChange={handleFormChange}
            error={errors.employee_NatID}
            placeholder="National ID Number"
          />
        </Col>
      </Row>

      <Row className="mb-3">
        <Col lg="6">
          <FormInput
            label="National ID Issue Date"
            id="employee_NatID_Date"
            name="employee_NatID_Date"
            type="date"
            value={form.employee_NatID_Date}
            onChange={handleFormChange}
            error={errors.employee_NatID_Date}
            required={false}
          />
        </Col>

        <Col lg="6">
          <FormInput
            label="National ID Expiry Date"
            id="employee_NatID_ExpDate"
            name="employee_NatID_ExpDate"
            type="date"
            value={form.employee_NatID_ExpDate}
            onChange={handleFormChange}
            error={errors.employee_NatID_ExpDate}
          />
        </Col>
      </Row>

      {/* Address Information */}
      <h6 className="mb-2 text-primary">Address Information</h6>
      <Row className="mb-3">
        <Col lg="6">
          <FormInput
            label="Address 1"
            id="employee_Address1"
            name="employee_Address1"
            value={form.employee_Address1}
            onChange={handleFormChange}
            error={errors.employee_Address1}
            placeholder="Primary Address"
            required={true}
          />
          <Button
            color="secondary"
            size="sm"
            onClick={copyAddress1ToAddress2}
            className="mt-1"
            type="button"
          >
            Copy to Address 2
          </Button>
        </Col>

        <Col lg="6">
          <FormInput
            label="Address 2"
            id="employee_Address2"
            name="employee_Address2"
            value={form.employee_Address2}
            onChange={handleFormChange}
            error={errors.employee_Address2}
            placeholder="Secondary Address"
            required={false}
          />
        </Col>
      </Row>

      {/* Employment Information */}
      <h6 className="mb-2 text-primary">Employment Information</h6>
      <Row className="mb-3">
        <Col lg="4">
          <FormSelect
            label="Company"
            id="employee_Business_Id"
            name="employee_Business_Id"
            options={businessOptions}
            value={businessOptions.find(option => option.value === form.employee_Business_Id) || null}
            onChange={(selectedOption) => handleSelectFormChange(selectedOption, 'employee_Business_Id')}
            error={errors.employee_Business_Id}
            required={true}
            isDisabled={isCompanyDisabled}
          />
        </Col>

        <Col lg="4">
          <FormSelect
            label="Department"
            id="employee_Department_ID"
            name="employee_Department_ID"
            options={departmentOptions}
            value={departmentOptions.find(option => option.value === form.employee_Department_ID) || null}
            onChange={(selectedOption) => handleSelectFormChange(selectedOption, 'employee_Department_ID')}
            error={errors.employee_Department_ID}
            required={true}
          />
        </Col>

        <Col lg="4">
          <FormSelect
            label="Designation"
            id="employee_Designation_ID"
            name="employee_Designation_ID"
            options={designationOptions}
            value={designationOptions.find(option => option.value === form.employee_Designation_ID) || null}
            onChange={(selectedOption) => handleSelectFormChange(selectedOption, 'employee_Designation_ID')}
            error={errors.employee_Designation_ID}
            required={true}
          />
        </Col>
      </Row>

      <Row className="mb-3">
        <Col lg="4">
          <FormSelect
            label="Branch"
            id="employee_Branch_Id"
            name="employee_Branch_Id"
            options={branchOptions}
            value={branchOptions.find(option => option.value === form.employee_Branch_Id) || null}
            onChange={(selectedOption) => handleSelectFormChange(selectedOption, 'employee_Branch_Id')}
            error={errors.employee_Branch_Id}
            required={true}
            isDisabled={isBranchDisabled}
          />
        </Col>

        <Col lg="4">
          <FormInput
            label="Join Date"
            id="employee_JoinDate"
            name="employee_JoinDate"
            type="date"
            value={form.employee_JoinDate}
            onChange={handleFormChange}
            error={errors.employee_JoinDate}
            required={true}
          />
        </Col>

        <Col lg="4">
          <div className="form-group">
            <Label className="col-form-label">
              Employment Type
            </Label>
            <Select
              id="employee_EmploymentType"
              name="employee_EmploymentType"
              value={{
                value: form.employee_EmploymentType || '',
                label: form.employee_EmploymentType || ''
              }}
              onChange={(selectedOption) => {
                const newValue = selectedOption ? selectedOption.value : '';
                setForm(prev => ({
                  ...prev,
                  employee_EmploymentType: newValue,
                  employee_ServiceEndDate: newValue === 'Permanent' ? '' : prev.employee_ServiceEndDate
                }));
              }}
              options={[
                { value: 'Permanent', label: 'Permanent' },
                { value: 'Contract', label: 'Contract' }
              ]}
              classNamePrefix="select2-selection"
              placeholder="Select Employment Type"
            />
            {errors.employee_EmploymentType && (
              <div className="invalid-feedback d-block">
                {errors.employee_EmploymentType}
              </div>
            )}
          </div>
        </Col>
      </Row>

      {form.employee_EmploymentType === 'Contract' && (
        <Row className="mb-3">
          <Col lg="4">
            <FormInput
              label="Service End Date"
              id="employee_ServiceEndDate"
              name="employee_ServiceEndDate"
              type="date"
              value={form.employee_ServiceEndDate}
              onChange={handleFormChange}
              error={errors.employee_ServiceEndDate}
              required={true}
            />
          </Col>
        </Row>
      )}

      {/* Salary Information */}
      <h6 className="mb-2 text-primary">Salary Information</h6>
      <Row className="mb-3">
        <Col lg="4">
          <FormInput
            label="Basic Salary"
            id="employee_BS"
            name="employee_BS"
            type="number"
            value={form.employee_BS}
            onChange={handleFormChange}
            error={errors.employee_BS}
            placeholder="Enter Basic Salary"
            required={true}
          />
        </Col>
        <Col lg="4">
          <FormInput
            label="House Rent Allowance"
            id="employee_HRA"
            name="employee_HRA"
            type="number"
            value={form.employee_HRA}
            onChange={handleFormChange}
            error={errors.employee_HRA}
            placeholder="Enter HRA"
            required={true}
          />
        </Col>
        <Col lg="4">
          <FormInput
            label="Transport Allowance"
            id="employee_TA"
            name="employee_TA"
            type="number"
            value={form.employee_TA}
            onChange={handleFormChange}
            error={errors.employee_TA}
            placeholder="Enter Transport Allowance"
            required={false}
          />
        </Col>
      </Row>

      <Row className="mb-3">
        <Col lg="4">
          <FormInput
            label="Other Allowance"
            id="employee_OA"
            name="employee_OA"
            type="number"
            value={form.employee_OA}
            onChange={handleFormChange}
            error={errors.employee_OA}
            placeholder="Enter Other Allowance"
            required={false}
          />
        </Col>
        <Col lg="4">
          <FormInput
            label="Food Allowance"
            id="employee_FoodAllowance"
            name="employee_FoodAllowance"
            type="number"
            value={form.employee_FoodAllowance}
            onChange={handleFormChange}
            error={errors.employee_FoodAllowance}
            placeholder="Enter Food Allowance"
            required={false}
          />
        </Col>
        <Col lg="4">
          <FormInput
            label="Communication Allowance"
            id="employee_CommunicationAllowance"
            name="employee_CommunicationAllowance"
            type="number"
            value={form.employee_CommunicationAllowance}
            onChange={handleFormChange}
            error={errors.employee_CommunicationAllowance}
            placeholder="Enter Communication Allowance"
            required={false}
          />
        </Col>
      </Row>

      <Row className="mb-3">
        <Col lg="4">
          <FormInput
            label="Utility Allowance"
            id="employee_UtilityAllowance"
            name="employee_UtilityAllowance"
            type="number"
            value={form.employee_UtilityAllowance}
            onChange={handleFormChange}
            error={errors.employee_UtilityAllowance}
            placeholder="Enter Utility Allowance"
            required={false}
          />
        </Col>
        <Col lg="4">
          <FormInput
            label="WPS Salary"
            id="wpsSalary"
            name="wpsSalary"
            type="number"
            value={form.wpsSalary}
            onChange={handleFormChange}
            error={errors.wpsSalary}
            placeholder="Enter WPS Salary"
            required={true}
          />
        </Col>
        <Col lg="4">
          <FormInput
            label="Gross Salary"
            id="employee_GS"
            name="employee_GS"
            type="number"
            value={form.employee_GS}
            className="bg-light"
            readOnly={true}
            disabled={true}
          />
        </Col>
      </Row>

      {/* Passport & Visa Information */}
      <h6 className="mb-2 text-primary">Passport & Visa Information</h6>
      <Row className="mb-3">
        <Col lg="4">
          <FormInput
            label="Passport Number"
            id="employee_PassportNumber"
            name="employee_PassportNumber"
            value={form.employee_PassportNumber}
            onChange={handleFormChange}
            error={errors.employee_PassportNumber}
            placeholder="Passport Number"
            required={true}
          />
        </Col>

        <Col lg="4">
          <FormInput
            label="Passport Issue Date"
            id="employee_Passport_Date"
            name="employee_Passport_Date"
            type="date"
            value={form.employee_Passport_Date}
            onChange={handleFormChange}
            error={errors.employee_Passport_Date}
            required={false}
          />
        </Col>

        <Col lg="4">
          <FormInput
            label="Passport Expiry Date"
            id="employee_Passport_ExpDate"
            name="employee_Passport_ExpDate"
            type="date"
            value={form.employee_Passport_ExpDate}
            onChange={handleFormChange}
            error={errors.employee_Passport_ExpDate}
            required={true}
          />
        </Col>
      </Row>

      <Row className="mb-3">
        <Col lg="4">
          <FormInput
            label="Visa Number"
            id="employee_VisaNumber"
            name="employee_VisaNumber"
            value={form.employee_VisaNumber}
            onChange={handleFormChange}
            error={errors.employee_VisaNumber}
            placeholder="Visa Number"
            required={true}
          />
        </Col>

        <Col lg="4">
          <FormInput
            label="Visa Issue Date"
            id="employee_Visa_Date"
            name="employee_Visa_Date"
            type="date"
            value={form.employee_Visa_Date}
            onChange={handleFormChange}
            error={errors.employee_Visa_Date}
            required={false}
          />
        </Col>

        <Col lg="4">
          <FormInput
            label="Visa Expiry Date"
            id="employee_Visa_ExpDate"
            name="employee_Visa_ExpDate"
            type="date"
            value={form.employee_Visa_ExpDate}
            onChange={handleFormChange}
            error={errors.employee_Visa_ExpDate}
            required={true}
          />
        </Col>
      </Row>

      {/* Labour Card and Employment Type Section */}
      <Row className="mb-3">
        <Col lg="4">
          <FormInput
            label="Labour Card Number"
            id="employee_LabourCardNumber"
            name="employee_LabourCardNumber"
            value={form.employee_LabourCardNumber}
            onChange={handleFormChange}
            error={errors.employee_LabourCardNumber}
            placeholder="Labour Card Number"
            required={true}
          />
        </Col>

        <Col lg="4">
          <FormInput
            label="Labour Card Expiry"
            id="employee_LabourCardExpiry"
            name="employee_LabourCardExpiry"
            type="date"
            value={form.employee_LabourCardExpiry}
            onChange={handleFormChange}
            error={errors.employee_LabourCardExpiry}
            required={true}
          />
        </Col>
      </Row>

      {/* Group Benefits Section */}
      <h6 className="mb-2 text-primary">Group Benefits</h6>
      <Row className="mb-3">
        <Col lg="6">
          <FormBenefitCheckbox
            label="Group Provided Housing"
            checkboxName="employee_HousingProvided"
            amountName="employee_HousingAmount"
            checkboxChecked={form.employee_HousingProvided}
            amountValue={form.employee_HousingAmount}
            onCheckboxChange={(e) => handleFormChange({
              target: {
                name: e.target.name,
                value: e.target.checked
              }
            })}
            onAmountChange={(e) => handleFormChange({
              target: {
                name: e.target.name,
                value: parseFloat(e.target.value) || 0
              }
            })}
          />
        </Col>
        <Col lg="6">
          <FormBenefitCheckbox
            label="Group Provided Telephone"
            checkboxName="employee_TelephoneProvided"
            amountName="employee_TelephoneAmount"
            checkboxChecked={form.employee_TelephoneProvided}
            amountValue={form.employee_TelephoneAmount}
            onCheckboxChange={(e) => handleFormChange({
              target: {
                name: e.target.name,
                value: e.target.checked
              }
            })}
            onAmountChange={(e) => handleFormChange({
              target: {
                name: e.target.name,
                value: parseFloat(e.target.value) || 0
              }
            })}
          />
        </Col>
      </Row>
      <Row className="mb-3">
        <Col lg="6">
          <FormBenefitCheckbox
            label="Group Provided Food"
            checkboxName="employee_FoodProvided"
            amountName="employee_FoodAmount"
            checkboxChecked={form.employee_FoodProvided}
            amountValue={form.employee_FoodAmount}
            onCheckboxChange={(e) => handleFormChange({
              target: {
                name: e.target.name,
                value: e.target.checked
              }
            })}
            onAmountChange={(e) => handleFormChange({
              target: {
                name: e.target.name,
                value: parseFloat(e.target.value) || 0
              }
            })}
          />
        </Col>
        <Col lg="6">
          <FormBenefitCheckbox
            label="Other Group Benefits"
            checkboxName="employee_OtherBenefits"
            amountName="employee_OtherBenefitsAmount"
            checkboxChecked={form.employee_OtherBenefits}
            amountValue={form.employee_OtherBenefitsAmount}
            onCheckboxChange={(e) => handleFormChange({
              target: {
                name: e.target.name,
                value: e.target.checked
              }
            })}
            onAmountChange={(e) => handleFormChange({
              target: {
                name: e.target.name,
                value: parseFloat(e.target.value) || 0
              }
            })}
          />
        </Col>
      </Row>

      {/* Image Upload */}
      <h6 className="mb-2 text-primary">Profile Image</h6>
      <Row className="mb-3">
        <Col lg="4">
          <FormGroup>
            <Label className="col-form-label">Profile Image</Label>
            <Input
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              className={errors.employee_ImagePath ? 'is-invalid' : ''}
            />
            {errors.employee_ImagePath && (
              <div className="invalid-feedback">
                {errors.employee_ImagePath}
              </div>
            )}
            <ImagePreview 
              file={selectedImage} 
              existingImage={form.employee_ImagePath} 
            />
          </FormGroup>
        </Col>
      </Row>

      {/* Action Buttons */}
      <Row>
        <Col lg="12">
          <div className="d-flex gap-2">
            <Button
              color="success"
              type="submit"
              disabled={loading}
            >
              {loading ? "Saving..." : (isEditing ? "Update Employee" : "Save Employee")}
            </Button>
            {isEditing && onCancel && (
              <Button
                color="secondary"
                type="button"
                onClick={onCancel}
              >
                Cancel Edit
              </Button>
            )}
          </div>
        </Col>
      </Row>
    </Form>
  );
};

export default EmployeeForm;
